import React from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../CommonLoader';

const NORMAL_COLOR = '#34A89A';
const MIN_COLOR = '#F59E0B';

const OccupancyByDay = ({ occupancyByDay, loading }) => {
  const { t } = useTranslation('dashboard');

  const rawData = Array.isArray(occupancyByDay?.data) ? occupancyByDay.data : [];
  const chartData = rawData.map((d) => ({
    label: d?.label ?? '',
    workingHours: Number(d?.workingHours) || 0,
    occupiedHours: Number(d?.occupiedHours) || 0,
    occupancyRate: Number(d?.occupancyRate) || 0,
  }));

  // The backend buckets by month for ranges longer than ~31 days (labels like
  // "Jun 2026") and by day for short ranges ("Jun 21") — reflect that in the
  // heading instead of always saying "by day".
  const isMonthly = chartData.some((d) => /\d{4}\s*$/.test(String(d.label)));
  const occHeading = isMonthly
    ? t('dashboard.occupancy_rate_by_month', 'Occupancy rate by month')
    : t('dashboard.occupancy_rate_by_day', 'Occupancy rate by day');

  const hasData = chartData.length > 0;
  const rates = chartData.map((d) => d.occupancyRate);
  const minRate = hasData ? Math.min(...rates) : 0;
  const maxRate = hasData ? Math.max(...rates) : 0;
  // Only highlight the lowest bar in amber when there is real spread (not uniform/all-zero).
  const hasSpread = maxRate > minRate;
  const allZero = hasData && chartData.every((d) => d.occupancyRate === 0);

  const xAxisInterval = chartData.length <= 8 ? 0 : 'preserveStartEnd';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0]?.payload || {};
      const rate = Number(d.occupancyRate) || 0;
      const occupied = Number(d.occupiedHours) || 0;
      const working = Number(d.workingHours) || 0;
      return (
        <div data-testid="occupancy-by-day-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p data-testid="occupancy-by-day-tooltip-rate" className="font-semibold text-gray-900">
            {d.label}: {Math.round(rate)}%
          </p>
          <p data-testid="occupancy-by-day-tooltip-hours" className="text-xs text-gray-600">
            {occupied.toLocaleString()}h / {working.toLocaleString()}h
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      data-testid="occupancy-by-day"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="bg-white rounded-xl p-4 border border-gray-200/50 shadow-md"
    >
      <div data-testid="occupancy-by-day-header" className="mb-4">
        <h3 data-testid="occupancy-by-day-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <BarChart3 data-testid="occupancy-by-day-icon" className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#4ABDBD]" />
          {occHeading}
        </h3>
        {allZero && (
          <p data-testid="occupancy-by-day-no-hours-hint" className="text-xs text-gray-400 mt-1">
            {t('dashboard.no_working_hours_configured', 'No working hours configured for this period')}
          </p>
        )}
      </div>

      {loading ? (
        <div data-testid="occupancy-by-day-loading" className="flex flex-col items-center justify-center py-12">
          <CommonLoader />
          <span data-testid="occupancy-by-day-loading-text" className="text-sm text-gray-600 mt-2">
            {t('dashboard.loading', 'Loading...')}
          </span>
        </div>
      ) : !hasData ? (
        <div data-testid="occupancy-by-day-empty-state" className="flex flex-col items-center justify-center py-12">
          <BarChart3 data-testid="occupancy-by-day-empty-icon" className="w-12 h-12 text-gray-300 mb-2" />
          <p data-testid="occupancy-by-day-empty-text" className="text-sm text-gray-600">
            {t('dashboard.no_occupancy_data', 'No occupancy data')}
          </p>
        </div>
      ) : (
        <div data-testid="occupancy-by-day-chart-container" className="h-56 sm:h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                interval={xAxisInterval}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <Tooltip cursor={{ fill: 'rgba(74,189,189,0.08)' }} content={<CustomTooltip />} />
              <Bar dataKey="occupancyRate" radius={[6, 6, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`occupancy-cell-${index}`}
                    fill={hasSpread && entry.occupancyRate > 0 && entry.occupancyRate === minRate ? MIN_COLOR : NORMAL_COLOR}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
};

export default OccupancyByDay;
